<template>
  <v-dialog width="800px"
            :value="value"
            @input="toggleModal()"
            persistent
            scrollable>
    <v-card class="kbLight">
      <v-toolbar class="kbPink" dark flat>
        <div class="text-h6">{{ selectedSequenceId ? $t('general.changeUserFromSequence') : $t('general.addUserToSequence') }}</div>
        <v-spacer></v-spacer>
        <v-btn icon @click="toggleModal()">
          <v-icon>clear</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text v-if="loadingPerson">
        <LoadingComponent height="400px"
                          :message="$t('general.loading')" />
      </v-card-text>

      <v-card-text v-else-if="!loadingPerson && Object.keys(selectedUser).length === 0 && value">
        <NotFound height="400px" :message="$t('general.userNotFound')" />
      </v-card-text>

      <v-card-text v-else-if="value">
        <v-form ref="form">
          <v-row>
            <v-col cols="12" class="pb-0 pt-6">
              <span class="kbDarkBlueText--text d-block text-subtitle-1 mb-2">
                {{ $t('general.selectASequence') }}
              </span>
              <v-autocomplete background-color="kbWhite"
                              color="kbDarkBlueText"
                              :items="filteredSequences"
                              item-text="name"
                              item-value="id"
                              :label="$t('general.selectASequence')"
                              :rules="[rules.required]"
                              :search-input.sync="search"
                              solo
                              :value="sequenceId"
                              @change="onSequenceIdChange($event)" />
            </v-col>

            <v-col cols="12" class="pb-0">
              <span class="kbDarkBlueText--text d-block text-subtitle-1 mb-2">
                {{ $t('general.pickAStep') }}
              </span>
              <v-select background-color="kbWhite"
                        color="kbDarkBlueText"
                        :label="$t('general.pickAStep')"
                        :items="sequenceSteps"
                        item-text="name"
                        item-value="id"
                        :rules="[rules.required]"
                        solo
                        v-model="stepId" />
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="red"
               class="mr-2"
               outlined
               @click="toggleModal()">
          {{ $t('general.cancel') }}
        </v-btn>
        <v-btn color="kbPink"
               dark
               :loading="editingUser"
               @click="submit()">
          {{ $t('general.saveChanges') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState } from 'vuex'
import notifications from '@/libs/notifications'
import rules from '@/libs/rules'
import LoadingComponent from '@/components/ui/LoadingComponent'
import NotFound from '@/components/ui/NotFound'
export default {
  components: { LoadingComponent, NotFound },
  computed: {
    ...mapState({
      selectedUser: state => state.users.selectedUser,
      sequences: state => state.sequences.sequences
    }),
    filteredSequences () {
      return this.selectedSequenceId
        ? this.sequences.filter(item => item.id !== this.selectedSequenceId)
        : this.sequences
    }
  },
  data () {
    return {
      editingUser: false,
      loadingPerson: false,
      rules,
      search: '',
      sequenceId: '',
      sequenceSteps: [],
      stepId: ''
    }
  },
  methods: {
    async addUserToSequence () {
      if (!this.$refs.form.validate()) return notifications.error(this.$t('general.youMustFillAllFieldsCorrectly'))
      try {
        this.editingUser = true
        const { userId, name, email, phone, extraData } = this.selectedUser
        await this.$store.dispatch('createSequencePerson', {
          appId: this.$store.getters.appUuid,
          sequenceId: this.sequenceId,
          stepId: this.stepId,
          data: { userId, name, email, phone, extraData }
        })
        this.editingUser = false
        notifications.success(this.$t('general.personSuccessfullyAddedToSequence'))
        this.toggleModal()
      } catch (e) {
        this.editingUser = false
      }
    },
    async changeUserFromSequence () {
      if (!this.$refs.form.validate()) return notifications.error(this.$t('general.youMustFillAllFieldsCorrectly'))
      try {
        this.editingUser = true
        const { userId, name, email, phone, extraData } = this.selectedUser
        await this.$store.dispatch('changeUserFromSequence', {
          appId: this.$store.getters.appUuid,
          data: { userId, name, email, phone, extraData, sequenceId: this.sequenceId, stepId: this.stepId }
        })
        this.editingUser = false
        notifications.success(this.$t('general.personSuccessfullyChangedFromSequence'))
        this.toggleModal()
      } catch (e) {
        this.editingUser = false
        notifications.error(e)
      }
    },
    clearSelectedUser () {
      this.$refs.form.reset()
      this.sequenceId = ''
      this.sequenceSteps = []
      this.stepId = ''
      this.$store.commit('SET_SELECTED_USER', {})
      this.$emit('clearSelectedUserId')
    },
    async getSequences () {
      try {
        await this.$store.dispatch('getSequences', { appId: this.$store.getters.appUuid })
      } catch (e) {
        notifications.error(e)
        this.toggleModal()
      }
    },
    async getSequenceSteps () {
      try {
        this.sequenceSteps = await this.$store.dispatch('getSequenceSteps',
          {
            appId: this.$store.getters.appUuid,
            sequenceId: this.sequenceId,
            setStepsLocally: true
          }
        )
      } catch (e) {
        this.sequenceSteps = []
        notifications.error(e)
      }
    },
    async getUser () {
      try {
        this.loadingPerson = true
        await this.$store.dispatch('getUser', { userId: this.selectedUserId })
        this.loadingPerson = false
      } catch (e) {
        this.loadingPerson = false
        console.log(e)
      }
    },
    onSequenceIdChange (sequenceId) {
      this.sequenceId = sequenceId
      if (sequenceId && sequenceId.trim()) {
        this.getSequenceSteps()
      }
    },
    submit () {
      this.selectedSequenceId ? this.changeUserFromSequence() : this.addUserToSequence()
    },
    toggleModal () {
      this.$emit('input', false)
      this.clearSelectedUser()
    }
  },
  props: {
    selectedSequenceId: {
      type: String,
      default: ''
    },
    selectedUserId: {
      type: String,
      default: ''
    },
    value: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    value (value) {
      if (value && this.selectedUserId) {
        this.getUser()
      }
    }
  }
}
</script>
